/**
 * Roadmap view: minimalist header (language + track selectors) and graph of nodes.
 * Subscription: the full roadmap is visible to everyone; only Programming Fundamentals is usable without
 * premium—other tracks appear locked and send users to subscribe when opened.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, matchPath, createSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useRoadmap } from '../../hooks/useRoadmap';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import {
  useLanguageTrackOrderGraph,
  orderTracksForLanguageRoadmap,
  pickLearningPathForLanguage
} from '../../hooks/useLanguageTrackOrderGraph';
import RoadmapList, { computeTrackSnippetMastery, isSnippetMastered, isSnippetStarted } from './RoadmapList';
import RoadmapLearnSidebar from './RoadmapLearnSidebar';
import {
  readRoadmapLearnHubMode,
  writeRoadmapLearnHubMode,
  ROADMAP_LEARN_MODES
} from './roadmapLearnHub';
import { defaultLearnRoleIdFromRoles, findRoadmapLearnRole } from './roadmapLearnRoles';
import usePublishedRoles from '../../hooks/usePublishedRoles';
import { useUserProfile, selectPrimaryRoleId, USER_PROFILE_QUERY_KEY } from '../../hooks/useUserProfile';
import { assignPrimaryRole } from '../../services/roleAssignmentService';
import { trackRoleSwitched } from '../../utils/analytics';
import { auth } from '../../firebase/firebaseConfig';
import * as roadmapService from '../../services/roadmapService';
import {
  formatLanguageDisplay,
  formatRoadmapTrackTitle,
  getTrackLanguageKey,
  ROADMAP_SCOPE_LABEL,
  trackMatchesLanguage,
  normalizeRoadmapLanguageKey
} from '../../utils/roadmapDisplay';
import { buildOrderedNodeGraph } from '../../utils/roadmapGraph';
import {
  LAST_ROADMAP_LANG_KEY,
  readStoredLastRoadmapLanguage,
  resolveGuestDefaultRoadmapLanguage,
  loadRecentSnippetByLanguage,
  sortLanguagesByRecentStudy
} from '../../utils/roadmapLanguageMenuStats';
import {
  captureLandingIntent,
  canonicalizeLearnSearchParams,
  readLanguageParamFromSearch,
  readRoleParamFromSearch,
} from '../../utils/landingIntent';
import {
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_MENU_SELECTED_LANG_STATE_KEY,
  ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY,
  ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_PATHS,
  ROADMAP_ROLE_ID_SEARCH_PARAM,
  roadmapGraphStateWithoutContinueDeepLink,
  roadmapGraphStateWithoutSelectedTrack
} from './roadmapPaths';
import { isRoadmapFreePublicTrack, userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import { loadRoadmapSnippetStatsByIds } from '../../services/profileAchievementStatsService';
import {
  collectStartedNotDueSnippetIds,
  loadRoadmapPracticeStartedCards
} from '../../utils/roadmapPracticeStartedSession';
import './RoadmapView.css';

/** JSON map of normalized language key → 'nodes' | 'learningPath' (single-track roadmap vs language track-order graph). */
const ROADMAP_GRAPH_SCOPE_STORAGE_KEY = (uid) => (uid ? `flashcode_roadmap_graph_scope_${uid}` : null);

function readRoadmapGraphScopeMap(userId) {
  if (!userId || typeof window === 'undefined') return {};
  const k = ROADMAP_GRAPH_SCOPE_STORAGE_KEY(userId);
  if (!k) return {};
  try {
    const raw = window.localStorage.getItem(k);
    if (!raw) return {};
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

/** @returns {'nodes' | 'learningPath' | null} */
function readRoadmapGraphScopeForLanguage(userId, langKey) {
  const map = readRoadmapGraphScopeMap(userId);
  const sk = normalizeRoadmapLanguageKey(langKey);
  const v = map[sk];
  return v === 'learningPath' || v === 'nodes' ? v : null;
}

function writeRoadmapGraphScopeForLanguage(userId, langKey, scope) {
  if (!userId || typeof window === 'undefined') return;
  const k = ROADMAP_GRAPH_SCOPE_STORAGE_KEY(userId);
  if (!k) return;
  try {
    const map = readRoadmapGraphScopeMap(userId);
    map[normalizeRoadmapLanguageKey(langKey)] = scope;
    window.localStorage.setItem(k, JSON.stringify(map));
  } catch {
    /* ignore quota / privacy mode */
  }
}

/** Restore the last language hub context when returning from Role → Language. */
function resolveLearnHubLanguageRestore({
  userId,
  lastVisitedRoadmapLang,
  availableLanguages,
  tracks
}) {
  const candidates = [
    lastVisitedRoadmapLang,
    readStoredLastRoadmapLanguage(userId),
    resolveGuestDefaultRoadmapLanguage(tracks),
    availableLanguages?.[0]
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const norm = normalizeRoadmapLanguageKey(String(raw));
    const match = (availableLanguages || []).find(
      (lang) => normalizeRoadmapLanguageKey(lang) === norm
    );
    if (match) return normalizeRoadmapLanguageKey(match);
  }
  return '';
}

export default function RoadmapView({ userId, onStartStudying, onGoToReview, lastCompletedNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPremiumUser } = useDirectPremiumCheck(userId);
  const isAnonymousGuest = Boolean(auth.currentUser?.isAnonymous);
  const [isSettingPrimaryRole, setIsSettingPrimaryRole] = useState(false);

  const handleSubscriptionRequired = useCallback(() => {
    window.dispatchEvent(new CustomEvent('navigateToSubscription'));
  }, []);

  const [learnHubMode, setLearnHubMode] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const raw = new URLSearchParams(window.location.search).get(ROADMAP_ROLE_ID_SEARCH_PARAM);
        if (raw != null && String(raw).trim()) {
          return ROADMAP_LEARN_MODES.role;
        }
      } catch {
        /* ignore malformed search */
      }
    }
    return readRoadmapLearnHubMode();
  });
  const isRoleLearnMode = learnHubMode === ROADMAP_LEARN_MODES.role;
  const isLanguageLearnMode = learnHubMode === ROADMAP_LEARN_MODES.language;
  const [graphScope, setGraphScope] = useState('nodes'); // 'nodes' | 'learningPath'
  const [pendingTrackPreview, setPendingTrackPreview] = useState({
    open: false,
    nodeId: null
  });
  const [lastVisitedRoadmapLang, setLastVisitedRoadmapLang] = useState(() => {
    if (typeof window === 'undefined' || !userId) return null;
    const k = LAST_ROADMAP_LANG_KEY(userId);
    try {
      return k ? window.localStorage.getItem(k) : null;
    } catch {
      return null;
    }
  });
  const [recentSnippetByLanguage, setRecentSnippetByLanguage] = useState(() => new Map());

  /** Learn routes: split-view hub (`/home/roadmap`) and graph deep-link (`/home/roadmap/graph`). */
  const isLearnMenuRoute = Boolean(
    matchPath({ path: ROADMAP_PATHS.menu, end: true }, location.pathname)
  );
  const isLearnGraphRoute = Boolean(
    matchPath({ path: ROADMAP_PATHS.graph, end: true }, location.pathname)
  );
  const showLearnSplitView = isLearnMenuRoute || isLearnGraphRoute;
  /** Legacy full-screen language menu — replaced by split view. */
  const showLanguageMenu = false;

  const [invalidLanguageNotice, setInvalidLanguageNotice] = useState(null);

  /** Query + navigation-state primitives (stable) for centralized language resolution inside `useRoadmap`. */
  const roadmapGraphSearchLangKey = useMemo(() => {
    try {
      const { normalized } = readLanguageParamFromSearch(location.search);
      return normalized || '';
    } catch {
      return '';
    }
  }, [location.search]);

  const rawNavMenuLang =
    location.state &&
      typeof location.state === 'object' &&
      !Array.isArray(location.state)
      ? location.state[ROADMAP_GRAPH_MENU_SELECTED_LANG_STATE_KEY]
      : undefined;
  const roadmapGraphNavMenuLangKey =
    rawNavMenuLang != null && String(rawNavMenuLang).trim()
      ? normalizeRoadmapLanguageKey(String(rawNavMenuLang))
      : '';

  const roadmapGraphNavTrackId = useMemo(() => {
    if (!location.state || typeof location.state !== 'object' || Array.isArray(location.state)) {
      return '';
    }
    const raw = location.state[ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY];
    return raw != null && String(raw).trim() ? String(raw).trim() : '';
  }, [location.state]);

  const roadmapSync = useMemo(
    () => ({
      showLanguageMenu,
      isGraphRoute:
        showLearnSplitView &&
        Boolean(roadmapGraphSearchLangKey || roadmapGraphNavMenuLangKey),
      graphSearchLangNorm: roadmapGraphSearchLangKey,
      graphNavMenuLangNorm: roadmapGraphNavMenuLangKey
    }),
    [
      showLanguageMenu,
      showLearnSplitView,
      roadmapGraphSearchLangKey,
      roadmapGraphNavMenuLangKey
    ]
  );

  const {
    tracks,
    selectedTrack,
    orderedNodes,
    nodeById,
    completedNodeIds,
    nodeScores,
    nodeLastAttempts,
    availableLanguages,
    tracksForLanguage,
    setSelectedLanguage,
    languageForTracks,
    selectTrack,
    clearSelection,
    tracksCatalogSig,
    isLoadingTracks,
    isLoadingNodes,
    error
  } = useRoadmap(userId, {
    roadmapSync,
    preferPublicFundamentalsTrack: !isPremiumUser,
    // Role paths mix languages; keep the clicked track (don't snap back to URL-lang PF).
    constrainSelectionToLanguage: !isRoleLearnMode
  });

  /** Latest catalog from hook — async work reads `tracksRef` so effects need not depend on array identity. */
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;

  /**
   * Canonical language for the graph + learning-path hook: derived in `useRoadmap` from URL, nav state,
   * localStorage, and catalog — no competing `useEffect` sync loops in this view.
   */
  const resolvedRoadmapLanguage =
    showLearnSplitView && languageForTracks !== 'all' ? languageForTracks : null;

  const resolvedRoadmapLangNorm = normalizeRoadmapLanguageKey(resolvedRoadmapLanguage || '');

  const selectedRoleId = useMemo(() => readRoleParamFromSearch(location.search), [location.search]);

  useEffect(() => {
    captureLandingIntent(location.search);
  }, [location.search]);

  /** Marketing aliases (`language`, `role`) → canonical query keys (`roadmapLang`, `roleId`). */
  useEffect(() => {
    if (!showLearnSplitView) return;
    const { changed, search } = canonicalizeLearnSearchParams(location.search);
    if (!changed) return;
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        state: location.state,
      },
      { replace: true }
    );
  }, [showLearnSplitView, location.search, location.pathname, location.state, navigate]);

  const { roles: publishedRoles, loading: rolesLoading } = usePublishedRoles({ subscribe: true });
  const { data: userProfileData } = useUserProfile(userId);
  const primaryRoleIdFromProfile = selectPrimaryRoleId(userProfileData?.userData);

  const resolvedDefaultRoleId = useMemo(
    () => primaryRoleIdFromProfile || defaultLearnRoleIdFromRoles(publishedRoles),
    [primaryRoleIdFromProfile, publishedRoles]
  );

  const selectedLearnRole = useMemo(
    () => findRoadmapLearnRole(selectedRoleId, publishedRoles),
    [selectedRoleId, publishedRoles]
  );

  const orderedTracksForRole = useMemo(() => {
    if (!selectedLearnRole?.tracks?.length || !tracks?.length) return [];
    const byId = new Map(tracks.map((t) => [t.trackId, t]));
    return selectedLearnRole.tracks
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((step) => byId.get(step.trackId))
      .filter(Boolean);
  }, [selectedLearnRole, tracks]);

  /**
   * Deep-link only: `?roleId=` in the URL opens the Role tab.
   * Must not depend on `learnHubMode` — otherwise switching back to Language
   * while roleId is still in the URL immediately re-forces Role mode.
   */
  useEffect(() => {
    if (!showLearnSplitView || !selectedRoleId) return;
    writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.role);
    setLearnHubMode(ROADMAP_LEARN_MODES.role);
  }, [showLearnSplitView, selectedRoleId]);

  /** Role tab with no `?roleId=` — default to Programmer (or first published role). */
  useEffect(() => {
    if (!showLearnSplitView || !isRoleLearnMode || selectedRoleId) return;
    if (rolesLoading) return;

    const roleId = resolvedDefaultRoleId;
    const params = createSearchParams(
      location.search.startsWith('?') ? location.search.slice(1) : location.search
    );
    params.set(ROADMAP_ROLE_ID_SEARCH_PARAM, roleId);
    navigate(
      {
        pathname: isLearnGraphRoute ? ROADMAP_PATHS.graph : ROADMAP_PATHS.menu,
        search: params.toString(),
        state: roadmapGraphStateWithoutSelectedTrack(location.state)
      },
      { replace: true }
    );
  }, [
    showLearnSplitView,
    isRoleLearnMode,
    selectedRoleId,
    rolesLoading,
    publishedRoles,
    resolvedDefaultRoleId,
    navigate,
    isLearnGraphRoute,
    location.search,
    location.state
  ]);

  const {
    pathEntries: learningPathEntries,
    error: learningPathError
  } = useLanguageTrackOrderGraph(userId, resolvedRoadmapLanguage, tracks);

  /**
   * Tracks for the list view, ordered by the language's learning-path entries (Programming
   * Fundamentals first, etc.) — matches the ordering the previous graph view used.
   * `orderTracksForLanguageRoadmap` keeps any tracks not in the path at the end alphabetically.
   */
  const orderedTracksForLanguage = useMemo(() => {
    if (!tracksForLanguage?.length || !resolvedRoadmapLanguage) {
      return tracksForLanguage || [];
    }
    return orderTracksForLanguageRoadmap(
      tracksForLanguage,
      learningPathEntries,
      resolvedRoadmapLanguage
    );
  }, [tracksForLanguage, learningPathEntries, resolvedRoadmapLanguage]);

  /**
   * Free tier (legacy single-track graph): stay on Programming Fundamentals.
   * Learn split view allows selecting Pro tracks for preview; study stays gated.
   */
  useEffect(() => {
    if (isPremiumUser || showLearnSplitView) return;
    const lang = languageForTracks;
    if (!lang || lang === 'all') return;
    const tr = tracksRef.current;
    const langTracks = tr.filter((t) => trackMatchesLanguage(t, lang));
    const fundamentals = langTracks.find((t) => isRoadmapFreePublicTrack(t));
    if (fundamentals) {
      if (!selectedTrack || !userCanAccessRoadmapTrack(false, selectedTrack.trackId, selectedTrack)) {
        selectTrack(fundamentals);
      }
    }
    // Never selectTrack(null) here: useRoadmap auto-select would immediately pick list[0] (often paywalled),
    // then this effect fires again → infinite update depth for free users without a fundamentals listing.
  }, [isPremiumUser, showLearnSplitView, languageForTracks, tracksCatalogSig, selectedTrack, selectTrack]);

  useEffect(() => {
    // Language Learn only: changing roadmapLang clears the open track.
    // Role Learn is cross-language — a stale URL lang must not wipe the clicked track.
    if (!showLearnSplitView || isRoleLearnMode) return;
    clearSelection();
  }, [showLearnSplitView, isRoleLearnMode, resolvedRoadmapLangNorm, clearSelection]);

  useEffect(() => {
    if (!showLearnSplitView || !isRoleLearnMode) return;
    clearSelection();
  }, [showLearnSplitView, isRoleLearnMode, selectedRoleId, clearSelection]);

  /** Unknown language slug in URL — fall back silently and show a one-line notice. */
  useEffect(() => {
    if (!showLearnSplitView || !availableLanguages.length) return;
    const { raw, normalized } = readLanguageParamFromSearch(location.search);
    if (!raw || !normalized) {
      setInvalidLanguageNotice(null);
      return;
    }
    const matched = availableLanguages.some(
      (lang) => normalizeRoadmapLanguageKey(lang) === normalized
    );
    if (matched) {
      setInvalidLanguageNotice(null);
      return;
    }
    setInvalidLanguageNotice(
      `"${raw}" is not available yet — showing ${formatLanguageDisplay(
        languageForTracks !== 'all' ? languageForTracks : availableLanguages[0]
      )} instead.`
    );
  }, [
    showLearnSplitView,
    availableLanguages,
    location.search,
    languageForTracks,
  ]);

  /** Default language on first visit to `/home/roadmap` (no `roadmapLang` in URL yet). */
  useEffect(() => {
    if (!isLearnMenuRoute || !tracksCatalogSig || !availableLanguages.length) return;
    if (roadmapGraphSearchLangKey) return;
    // Role deep-links use `?roleId=` only — do not inject `roadmapLang` (that would strip roleId).
    if (selectedRoleId) return;
    if (isRoleLearnMode) return;
    const preferred =
      readStoredLastRoadmapLanguage(userId) ||
      resolveGuestDefaultRoadmapLanguage(tracksRef.current) ||
      availableLanguages[0];
    if (!preferred) return;
    const params = createSearchParams(
      location.search.startsWith('?') ? location.search.slice(1) : location.search
    );
    params.set(ROADMAP_GRAPH_LANG_SEARCH_PARAM, normalizeRoadmapLanguageKey(preferred));
    navigate(
      {
        pathname: ROADMAP_PATHS.menu,
        search: params.toString()
      },
      { replace: true }
    );
  }, [
    isLearnMenuRoute,
    tracksCatalogSig,
    availableLanguages,
    roadmapGraphSearchLangKey,
    selectedRoleId,
    isRoleLearnMode,
    userId,
    navigate,
    location.search
  ]);

  useEffect(() => {
    if (!userId || typeof window === 'undefined') return;
    const k = LAST_ROADMAP_LANG_KEY(userId);
    if (!k) return;
    try {
      setLastVisitedRoadmapLang(window.localStorage.getItem(k));
    } catch {
      /* ignore */
    }
  }, [userId, showLearnSplitView]);

  useEffect(() => {
    if (!userId || !tracksCatalogSig) {
      setRecentSnippetByLanguage(new Map());
      return undefined;
    }

    let cancelled = false;
    loadRecentSnippetByLanguage(userId, tracksRef.current).then((map) => {
      if (!cancelled) setRecentSnippetByLanguage(map);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, tracksCatalogSig, showLearnSplitView, location.pathname, lastCompletedNode?.nodeId]);

  const languagesForLearnSidebar = useMemo(
    () =>
      sortLanguagesByRecentStudy(availableLanguages, recentSnippetByLanguage, {
        prioritizeLanguage: lastVisitedRoadmapLang
      }),
    [availableLanguages, recentSnippetByLanguage, lastVisitedRoadmapLang]
  );

  /** Keep menu “last visited” label aligned with derived graph language (persistence runs in `useRoadmap`). */
  useEffect(() => {
    if (!userId || !showLearnSplitView) return;
    const lang = languageForTracks;
    if (!lang || lang === 'all') return;
    setLastVisitedRoadmapLang(lang);
  }, [userId, showLearnSplitView, languageForTracks]);

  /** Restore single-track roadmap vs language track-order graph from session storage. */
  useEffect(() => {
    if (!showLearnSplitView) return;
    if (!userId || !resolvedRoadmapLangNorm) return;
    const stored = readRoadmapGraphScopeForLanguage(userId, resolvedRoadmapLanguage);
    const next = stored === 'learningPath' ? 'learningPath' : 'nodes';
    setGraphScope((prev) => (prev === next ? prev : next));
  }, [showLearnSplitView, userId, resolvedRoadmapLangNorm, resolvedRoadmapLanguage]);

  /** Profile / deep-link: open a specific track on the graph (one-shot `location.state`). */
  useEffect(() => {
    if (!showLearnSplitView || !roadmapGraphNavTrackId || !tracksCatalogSig) return;

    const track = tracksRef.current.find((t) => String(t.trackId) === roadmapGraphNavTrackId);
    const navState =
      location.state && typeof location.state === 'object' && !Array.isArray(location.state)
        ? location.state
        : {};
    const shouldOpenTrackPreview = Boolean(navState[ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]);
    const resumeNodeIdRaw = navState[ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY];
    const resumeNodeId =
      resumeNodeIdRaw != null && String(resumeNodeIdRaw).trim()
        ? String(resumeNodeIdRaw).trim()
        : null;

    const clearNavTrackState = () => {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: roadmapGraphStateWithoutContinueDeepLink(location.state) }
      );
    };

    if (!track) {
      clearNavTrackState();
      return;
    }

    // Allow free users to deep-link into Pro track preview; study remains gated.
    selectTrack(track);
    setGraphScope('nodes');
    if (shouldOpenTrackPreview) {
      setPendingTrackPreview({
        open: true,
        nodeId: resumeNodeId
      });
    }
    if (userId && resolvedRoadmapLanguage) {
      writeRoadmapGraphScopeForLanguage(userId, resolvedRoadmapLanguage, 'nodes');
    }
    clearNavTrackState();
  }, [
    showLearnSplitView,
    roadmapGraphNavTrackId,
    tracksCatalogSig,
    location.pathname,
    location.search,
    location.state,
    userId,
    resolvedRoadmapLanguage,
    selectTrack,
    navigate
  ]);

  const handleConsumePendingTrackPreview = useCallback(() => {
    setPendingTrackPreview({ open: false, nodeId: null });
  }, []);

  const roadmapListContinuePreviewProps = {
    initialOpenTrackPreview: pendingTrackPreview.open,
    initialFocusNodeId: pendingTrackPreview.nodeId,
    onInitialOpenTrackPreviewConsumed: handleConsumePendingTrackPreview
  };

  // Merge Firestore completedNodeIds with optimistic lastCompletedNode (so node turns green immediately after test pass)
  const displayCompletedNodeIds = useMemo(() => {
    const set = new Set(completedNodeIds || []);
    if (lastCompletedNode?.nodeId && lastCompletedNode?.trackId && selectedTrack?.trackId === lastCompletedNode.trackId) {
      set.add(lastCompletedNode.nodeId);
    }
    return Array.from(set);
  }, [completedNodeIds, lastCompletedNode, selectedTrack?.trackId]);

  const displayCurrentNodeId = useMemo(() => {
    const set = new Set(displayCompletedNodeIds);
    const first = orderedNodes.find(n => !set.has(n.id));
    return first ? first.id : null;
  }, [orderedNodes, displayCompletedNodeIds]);

  /**
   * Node shown in the stationary footer's center pill.
   * First-incomplete in the selected track; falls back to the last node if the whole
   * track is done so the footer still reflects where the user is.
   */
  const footerTargetNode = useMemo(() => {
    if (!nodeById) return null;
    if (displayCurrentNodeId) return nodeById.get(displayCurrentNodeId) || null;
    if (orderedNodes?.length) return orderedNodes[orderedNodes.length - 1];
    return null;
  }, [displayCurrentNodeId, nodeById, orderedNodes]);

  /**
   * Tracks which topic action is currently loading.
   * Shape: `{ nodeId, kind: 'study' | 'test' }` or `null`.
   * Used so each per-topic Study / Test button can show its own "…" busy state without
   * a global flag — the user can only click one button at a time, so a single slot suffices.
   */
  const [loadingNodeAction, setLoadingNodeAction] = useState(null);
  /** SRS stats per snippet id for all tracks in the selected language (deduped). */
  const [snippetStatsById, setSnippetStatsById] = useState({});
  /** Map<trackId, nodes[]> — nodes loaded for every track in the current language (used by the list view). */
  const [nodesByTrackId, setNodesByTrackId] = useState(() => new Map());
  /**
   * Map<trackId, Set<nodeId>> — completed node IDs (i.e. topics whose tests have been passed)
   * for every track in the current language. Loaded once per language so the list view can
   * compute test-based progress for tracks the user hasn't currently selected. The selected
   * track's set is kept live by `useRoadmap` via `completedNodeIds`; for that track we prefer
   * the live data over this snapshot.
   */
  const [completedNodesByTrackId, setCompletedNodesByTrackId] = useState(() => new Map());
  /**
   * Set<snippetId> — snippets due for review today (started + SRS due, same as Study
   * Mode yellow dots). Loaded once alongside snippet stats so the list view can show a
   * per-track "snippets due" count without each track running its own progress query.
   */
  const [dueSnippetIds, setDueSnippetIds] = useState(() => new Set());

  /** Tracks shown in the list pane — language path or role path (cross-language). */
  const listViewTracks = useMemo(() => {
    if (!showLearnSplitView) return [];
    if (isRoleLearnMode && selectedLearnRole) return orderedTracksForRole;
    if (!languageForTracks || languageForTracks === 'all') return [];
    return orderedTracksForLanguage;
  }, [
    showLearnSplitView,
    isRoleLearnMode,
    selectedLearnRole,
    orderedTracksForRole,
    languageForTracks,
    orderedTracksForLanguage
  ]);

  const listViewTracksSig = useMemo(() => {
    if (!listViewTracks.length) return '';
    const scope = isRoleLearnMode
      ? `role:${selectedLearnRole?.roleId || selectedLearnRole?.id || ''}`
      : `lang:${normalizeRoadmapLanguageKey(languageForTracks)}`;
    return `${scope}:${listViewTracks.map((t) => String(t.trackId)).sort().join('|')}`;
  }, [listViewTracks, isRoleLearnMode, selectedLearnRole, languageForTracks]);

  // Load node graphs + snippet stats + per-track completion for list view tracks (language or role).
  useEffect(() => {
    if (!showLearnSplitView || !userId || !listViewTracksSig) {
      setSnippetStatsById((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      setNodesByTrackId((prev) => (prev.size === 0 ? prev : new Map()));
      setCompletedNodesByTrackId((prev) => (prev.size === 0 ? prev : new Map()));
      setDueSnippetIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    const tracksToLoad = listViewTracks;
    if (!tracksToLoad.length) {
      setSnippetStatsById((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      setNodesByTrackId((prev) => (prev.size === 0 ? prev : new Map()));
      setCompletedNodesByTrackId((prev) => (prev.size === 0 ? prev : new Map()));
      setDueSnippetIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    let cancelled = false;
    (async () => {
      const nodeLists = await Promise.all(
        tracksToLoad.map((t) => roadmapService.loadTrackNodes(t.trackId))
      );
      if (cancelled) return;
      const nextNodesByTrackId = new Map();
      tracksToLoad.forEach((t, i) => {
        const ordered = buildOrderedNodeGraph(nodeLists[i] || []);
        nextNodesByTrackId.set(t.trackId, ordered);
      });
      setNodesByTrackId(nextNodesByTrackId);
      /**
       * Per-track test-pass snapshot for cookbook row state (topic dots / isCompleted).
       * Track progress bars use snippet mastery instead; the selected track's live
       * `completedNodeIds` subscription takes precedence over this snapshot.
       */
      const completionResults = await Promise.all(
        tracksToLoad.map(async (t) => {
          try {
            const prog = await roadmapService.loadTrackProgress(userId, t.trackId);
            return [t.trackId, new Set(prog?.completedNodeIds || [])];
          } catch {
            return [t.trackId, new Set()];
          }
        })
      );
      if (cancelled) return;
      setCompletedNodesByTrackId(new Map(completionResults));
      const unique = new Set();
      nodeLists.flat().forEach((node) => {
        (node.snippetIds || []).forEach((id) => unique.add(id));
      });
      const allSnippetIds = Array.from(unique);
      if (allSnippetIds.length === 0) {
        setSnippetStatsById((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        setDueSnippetIds((prev) => (prev.size === 0 ? prev : new Set()));
        return;
      }
      try {
        const { statsBySnippetId, dueSnippetIds: dueSet } = await loadRoadmapSnippetStatsByIds(
          userId,
          allSnippetIds,
          { withDueSnippetIds: true }
        );
        if (cancelled) return;
        setSnippetStatsById(statsBySnippetId);
        setDueSnippetIds(dueSet);
      } catch {
        if (!cancelled) {
          setSnippetStatsById({});
          setDueSnippetIds(new Set());
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId, listViewTracksSig, showLearnSplitView, listViewTracks]);

  /**
   * Tracks-completed count for the page header. A track is complete when every snippet
   * in it is mastered — matches the list view's track progress bar.
   */
  const trackProgress = useMemo(() => {
    const total = listViewTracks.length;
    if (total === 0) return { completed: 0, total: 0 };
    let completed = 0;
    for (const t of listViewTracks) {
      const nodes = nodesByTrackId.get(t.trackId);
      if (!Array.isArray(nodes) || nodes.length === 0) continue;
      const { total: snippetTotal, mastered } = computeTrackSnippetMastery(
        nodes,
        snippetStatsById
      );
      if (snippetTotal > 0 && mastered === snippetTotal) completed += 1;
    }
    return { completed, total };
  }, [listViewTracks, nodesByTrackId, snippetStatsById]);

  const currentLanguage = selectedTrack?.language || 'javascript';

  const resolveRoadmapCookbook = useCallback(
    async (track, node, snippets) => {
      const trackId = track?.trackId;
      const langKeyForCookbook = getTrackLanguageKey(track) || currentLanguage;
      const trackTitleForCookbook =
        formatRoadmapTrackTitle(langKeyForCookbook, track.title, track) || track.title || '';
      return roadmapService.ensureRoadmapCookbookForNode(
        userId,
        trackId,
        node,
        snippets,
        langKeyForCookbook,
        trackTitleForCookbook
      );
    },
    [userId, currentLanguage]
  );

  const startRoadmapStudy = useCallback(
    (track, node, session, extras = {}) => {
      if (!onStartStudying || !session?.cookbookId) return;
      const payload = {
        deckId: session.cookbookId,
        trackId: track?.trackId,
        nodeId: node?.id,
        ...extras
      };
      // Due/started queues filter cards in StudyLogic.loadDeckAndCards — skip preloaded cards there.
      if (!extras.dueOnly && !extras.startedOnly) {
        payload.cards = session.cards;
      }
      onStartStudying(payload);
    },
    [onStartStudying]
  );

  /**
   * Start study mode for a specific snippet inside a topic. Ensures the topic's cookbook
   * exists (creating it on first study just like {@link handleStudyNode}), then opens study
   * mode with `startCardId` set to the snippet id. {@link StudyLogic} falls back to matching
   * `roadmapSnippetId` so the original snippet id resolves to the right card document.
   *
   * Takes the owning {@param track} explicitly so snippet clicks work in any expanded track,
   * not only the currently selected one.
   */
  const handleStudySnippet = useCallback(async (track, node, snippetId) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId || !snippetId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      startRoadmapStudy(track, node, session, { startCardId: snippetId });
    } catch (err) {
      console.error('RoadmapView: failed to start study for snippet', err);
    }
  }, [
    userId,
    onStartStudying,
    currentLanguage,
    isPremiumUser,
    handleSubscriptionRequired,
    resolveRoadmapCookbook,
    startRoadmapStudy
  ]);

  /**
   * Start study mode for a topic. Takes the owning {@param track} explicitly so the in-drawer
   * header works for any expanded track, not only the currently selected one.
   */
  const handleStudyNode = useCallback(async (track, node) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    setLoadingNodeAction({ nodeId: node.id, kind: 'study' });
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      startRoadmapStudy(track, node, session);
    } catch (err) {
      console.error('RoadmapView: failed to start study for node', err);
    } finally {
      setLoadingNodeAction(null);
    }
  }, [userId, onStartStudying, currentLanguage, isPremiumUser, handleSubscriptionRequired, resolveRoadmapCookbook, startRoadmapStudy]);

  /**
   * Review a due snippet from the language review hub — opens study on that card with due-only queue.
   */
  const handleReviewDueSnippet = useCallback(async (track, node, snippetId) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId || !snippetId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      startRoadmapStudy(track, node, session, {
        startCardId: snippetId,
        dueOnly: true
      });
    } catch (err) {
      console.error('RoadmapView: failed to start review for due snippet', err);
    }
  }, [
    userId,
    onStartStudying,
    currentLanguage,
    isPremiumUser,
    handleSubscriptionRequired,
    resolveRoadmapCookbook,
    startRoadmapStudy
  ]);

  /**
   * Start the next unstudied snippet in a topic — first snippet in cookbook order with no reviews yet.
   * When every snippet is already started, opens the cookbook for general study instead.
   */
  const handleStartNode = useCallback(async (track, node) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    const firstUnstarted = (node.snippetIds || []).find(
      (id) => !isSnippetStarted(snippetStatsById?.[id])
    );

    setLoadingNodeAction({ nodeId: node.id, kind: 'start' });
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      startRoadmapStudy(
        track,
        node,
        session,
        firstUnstarted ? { startCardId: firstUnstarted } : {}
      );
    } catch (err) {
      console.error('RoadmapView: failed to start next snippet for node', err);
    } finally {
      setLoadingNodeAction(null);
    }
  }, [
    userId,
    onStartStudying,
    currentLanguage,
    isPremiumUser,
    handleSubscriptionRequired,
    snippetStatsById,
    resolveRoadmapCookbook,
    startRoadmapStudy
  ]);

  /**
   * Practice started-but-not-due snippets — builds a cross-cookbook study queue for the
   * whole track or a single cookbook (node) when {@param node} is provided.
   */
  const handlePracticeStartedTrack = useCallback(async (track, node = null) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !trackId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }

    const nodes = node
      ? [node]
      : nodesByTrackId.get(trackId) || orderedNodes || [];
    const snippetIds = collectStartedNotDueSnippetIds(
      nodes,
      snippetStatsById,
      dueSnippetIds,
      node?.id
    );
    if (snippetIds.length === 0) return;

    setLoadingNodeAction({
      trackId,
      nodeId: node?.id || null,
      kind: 'practiceStarted'
    });
    try {
      const cards = await loadRoadmapPracticeStartedCards(
        userId,
        track,
        nodes,
        snippetIds,
        resolveRoadmapCookbook
      );
      if (cards.length === 0) return;

      const firstCard = cards[0];
      onStartStudying({
        practiceStartedSession: true,
        trackId,
        nodeId: node?.id || null,
        cards,
        startCardId: firstCard.roadmapSnippetId || firstCard.id
      });
    } catch (err) {
      console.error('RoadmapView: failed to start practice for started snippets', err);
    } finally {
      setLoadingNodeAction(null);
    }
  }, [
    userId,
    onStartStudying,
    orderedNodes,
    nodesByTrackId,
    snippetStatsById,
    dueSnippetIds,
    isPremiumUser,
    handleSubscriptionRequired,
    resolveRoadmapCookbook
  ]);
  const handleContinueNode = useCallback(async (track, node) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    setLoadingNodeAction({ nodeId: node.id, kind: 'continue' });
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      const firstUnmastered = (node.snippetIds || []).find(
        (id) => !isSnippetMastered(snippetStatsById?.[id])
      );
      startRoadmapStudy(track, node, session, {
        startCardId: firstUnmastered || undefined
      });
    } catch (err) {
      console.error('RoadmapView: failed to continue study for node', err);
    } finally {
      setLoadingNodeAction(null);
    }
  }, [
    userId,
    onStartStudying,
    currentLanguage,
    isPremiumUser,
    handleSubscriptionRequired,
    snippetStatsById,
    resolveRoadmapCookbook,
    startRoadmapStudy
  ]);

  const handleTestNode = useCallback(async (track, node) => {
    const trackId = track?.trackId;
    if (!userId || !onStartStudying || !node?.snippetIds?.length || !trackId) return;
    if (!userCanAccessRoadmapTrack(isPremiumUser, trackId, track)) {
      handleSubscriptionRequired();
      return;
    }
    setLoadingNodeAction({ nodeId: node.id, kind: 'test' });
    try {
      const snippets = await roadmapService.loadSnippetsByIds(node.snippetIds);
      if (snippets.length === 0) return;
      const session = await resolveRoadmapCookbook(track, node, snippets);
      startRoadmapStudy(track, node, session, { isTestMode: true });
    } catch (err) {
      console.error('RoadmapView: failed to start test for node', err);
    } finally {
      setLoadingNodeAction(null);
    }
  }, [userId, onStartStudying, currentLanguage, isPremiumUser, handleSubscriptionRequired, resolveRoadmapCookbook, startRoadmapStudy]);

  const handleContinueStudy = useCallback(() => {
    if (!selectedTrack || !footerTargetNode) return;
    handleStudyNode(selectedTrack, footerTargetNode);
  }, [selectedTrack, footerTargetNode, handleStudyNode]);

  /** Track row click: open track for preview (cookbooks/snippets). Study actions stay subscription-gated. */
  const handleSelectTrack = useCallback(
    (track) => {
      if (!track) return;
      selectTrack(track);
      setGraphScope('nodes');
      if (userId && resolvedRoadmapLanguage) {
        writeRoadmapGraphScopeForLanguage(userId, resolvedRoadmapLanguage, 'nodes');
      }
    },
    [selectTrack, userId, resolvedRoadmapLanguage]
  );

  const handleLanguageSelect = useCallback(
    (lang) => {
      const params = createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: normalizeRoadmapLanguageKey(lang)
      });
      navigate({
        pathname: isLearnGraphRoute ? ROADMAP_PATHS.graph : ROADMAP_PATHS.menu,
        search: params.toString(),
        state: roadmapGraphStateWithoutSelectedTrack(location.state)
      });
    },
    [navigate, isLearnGraphRoute, location.state]
  );

  const handleLearnModeChange = useCallback(
    (mode) => {
      const next =
        mode === ROADMAP_LEARN_MODES.role ? ROADMAP_LEARN_MODES.role : ROADMAP_LEARN_MODES.language;
      writeRoadmapLearnHubMode(next);
      setLearnHubMode(next);

      const params = createSearchParams(
        location.search.startsWith('?') ? location.search.slice(1) : location.search
      );
      if (next === ROADMAP_LEARN_MODES.language) {
        params.delete(ROADMAP_ROLE_ID_SEARCH_PARAM);
        if (!params.get(ROADMAP_GRAPH_LANG_SEARCH_PARAM)) {
          const restored = resolveLearnHubLanguageRestore({
            userId,
            lastVisitedRoadmapLang,
            availableLanguages,
            tracks: tracksRef.current
          });
          if (restored) {
            params.set(ROADMAP_GRAPH_LANG_SEARCH_PARAM, restored);
          }
        }
      } else if (!params.get(ROADMAP_ROLE_ID_SEARCH_PARAM)) {
        params.set(ROADMAP_ROLE_ID_SEARCH_PARAM, resolvedDefaultRoleId);
      }
      const search = params.toString();
      navigate(
        {
          pathname: isLearnGraphRoute ? ROADMAP_PATHS.graph : ROADMAP_PATHS.menu,
          search,
          state: roadmapGraphStateWithoutSelectedTrack(location.state)
        },
        { replace: true }
      );
    },
    [
      navigate,
      isLearnGraphRoute,
      location.state,
      location.search,
      userId,
      lastVisitedRoadmapLang,
      availableLanguages,
      publishedRoles,
      resolvedDefaultRoleId
    ]
  );

  const handleSelectRole = useCallback(
    (role) => {
      if (!role?.id) return;
      const params = createSearchParams(
        location.search.startsWith('?') ? location.search.slice(1) : location.search
      );
      params.set(ROADMAP_ROLE_ID_SEARCH_PARAM, role.id);
      navigate({
        pathname: isLearnGraphRoute ? ROADMAP_PATHS.graph : ROADMAP_PATHS.menu,
        search: params.toString(),
        state: roadmapGraphStateWithoutSelectedTrack(location.state)
      });
    },
    [navigate, isLearnGraphRoute, location.state, location.search]
  );

  const roleScopeHeader = useMemo(() => {
    if (!selectedLearnRole) return null;
    return {
      title: selectedLearnRole.title,
      subtitle: selectedLearnRole.promise || null,
      iconClass: selectedLearnRole.iconClass || null
    };
  }, [selectedLearnRole]);

  const selectedLearnRoleId = selectedLearnRole?.roleId || selectedLearnRole?.id || null;
  const isViewingPrimaryRolePath = Boolean(
    selectedLearnRoleId &&
      primaryRoleIdFromProfile &&
      selectedLearnRoleId === primaryRoleIdFromProfile
  );
  const canSetRolePath = Boolean(userId && !isAnonymousGuest && selectedLearnRoleId);

  const handleSetAsRolePath = useCallback(async () => {
    if (!canSetRolePath || !selectedLearnRoleId || isSettingPrimaryRole) return;
    if (selectedLearnRoleId === primaryRoleIdFromProfile) return;

    setIsSettingPrimaryRole(true);
    try {
      const previousRoleId = primaryRoleIdFromProfile;
      await assignPrimaryRole(userId, selectedLearnRoleId);
      trackRoleSwitched({
        role_id: selectedLearnRoleId,
        previous_role_id: previousRoleId || null,
        surface: 'roadmap-list'
      });
      queryClient.setQueryData([USER_PROFILE_QUERY_KEY, userId], (prev) => {
        if (!prev?.userData) return prev;
        return {
          ...prev,
          userData: {
            ...prev.userData,
            primaryRoleId: selectedLearnRoleId
          }
        };
      });
      await queryClient.invalidateQueries([USER_PROFILE_QUERY_KEY, userId]);
    } catch (err) {
      console.error('[RoadmapView] set primary role failed:', err);
    } finally {
      setIsSettingPrimaryRole(false);
    }
  }, [
    canSetRolePath,
    selectedLearnRoleId,
    isSettingPrimaryRole,
    primaryRoleIdFromProfile,
    userId,
    queryClient
  ]);

  const combinedError = error || learningPathError;

  const showNodesError = combinedError && graphScope === 'nodes';
  const showListLoading =
    isRoleLearnMode
      ? (rolesLoading || isLoadingTracks) && orderedTracksForRole.length === 0
      : isLoadingTracks && orderedTracksForLanguage.length === 0;

  const listFooterContext = useMemo(() => {
    if (!footerTargetNode || !selectedTrack) return null;
    const trackIdx = listViewTracks.findIndex(
      (t) => t.trackId === selectedTrack.trackId
    );
    if (trackIdx < 0) return null;
    const trackNumber = String(trackIdx + 1).padStart(2, '0');
    const trackLang = getTrackLanguageKey(selectedTrack) || resolvedRoadmapLanguage || 'javascript';
    const trackLabel = formatRoadmapTrackTitle(
      trackLang,
      selectedTrack.title,
      selectedTrack
    );
    return {
      trackNumber,
      trackLabel,
      cookbookTitle: footerTargetNode.title || 'Cookbook'
    };
  }, [
    footerTargetNode,
    selectedTrack,
    listViewTracks,
    resolvedRoadmapLanguage
  ]);

  return (
    <div className="roadmap-view">
      {invalidLanguageNotice ? (
        <div className="roadmap-view__learn-intent-notice" role="status">
          {invalidLanguageNotice}
        </div>
      ) : null}
      {showNodesError ? (
        <div className="roadmap-view__error">
          <p>{combinedError}</p>
        </div>
      ) : (
        <main className="roadmap-view__main">
          {showLearnSplitView ? (
            <div className="roadmap-view__split">
              <div className="roadmap-view__split-left-pane">
                <RoadmapLearnSidebar
                  languages={languagesForLearnSidebar}
                  roles={publishedRoles}
                  learnMode={learnHubMode}
                  onLearnModeChange={handleLearnModeChange}
                  selectedLanguage={resolvedRoadmapLanguage}
                  selectedRole={selectedLearnRole}
                  isLoadingTracks={isLoadingTracks}
                  lastVisitedLanguage={lastVisitedRoadmapLang}
                  onSelectLanguage={handleLanguageSelect}
                  onSelectRole={handleSelectRole}
                />
              </div>
              <div className="roadmap-view__split-right-pane">
                <motion.div
                  key={
                    isRoleLearnMode
                      ? selectedLearnRole?.id || 'no-role'
                      : resolvedRoadmapLangNorm || 'no-lang'
                  }
                  className="roadmap-view__animate-panel roadmap-view__animate-panel--graph"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                >
                  {isLanguageLearnMode && resolvedRoadmapLanguage ? (
                    <RoadmapList
                      language={resolvedRoadmapLanguage}
                      tracks={orderedTracksForLanguage}
                      selectedTrack={selectedTrack}
                      orderedNodes={orderedNodes}
                      completedNodeIds={displayCompletedNodeIds}
                      currentNodeId={displayCurrentNodeId}
                      nodeScores={nodeScores}
                      nodeLastAttempts={nodeLastAttempts}
                      nodesByTrackId={nodesByTrackId}
                      completedNodesByTrackId={completedNodesByTrackId}
                      dueSnippetIds={dueSnippetIds}
                      snippetStatsById={snippetStatsById}
                      trackProgress={trackProgress}
                      isPremiumUser={isPremiumUser}
                      isLoadingTracks={showListLoading}
                      isLoadingNodes={Boolean(selectedTrack && isLoadingNodes)}
                      footerContext={listFooterContext}
                      onSelectTrack={handleSelectTrack}
                      onStudySnippet={handleStudySnippet}
                      onStudyNode={handleStudyNode}
                      onStartNode={handleStartNode}
                      onContinueNode={handleContinueNode}
                      onPracticeStarted={handlePracticeStartedTrack}
                      onReviewDueSnippet={handleReviewDueSnippet}
                      loadingNodeAction={loadingNodeAction}
                      onGoToReview={onGoToReview}
                      onContinueStudy={handleContinueStudy}
                      continueStudyLabel={footerTargetNode?.title || null}
                      continueStudyDisabled={!selectedTrack || !footerTargetNode}
                      {...roadmapListContinuePreviewProps}
                    />
                  ) : isLanguageLearnMode ? (
                    <div className="roadmap-view__split-placeholder">
                      <p>Select a language to view its tracks.</p>
                    </div>
                  ) : selectedLearnRole ? (
                    <RoadmapList
                      language={null}
                      tracks={orderedTracksForRole}
                      selectedTrack={selectedTrack}
                      orderedNodes={orderedNodes}
                      completedNodeIds={displayCompletedNodeIds}
                      currentNodeId={displayCurrentNodeId}
                      nodeScores={nodeScores}
                      nodeLastAttempts={nodeLastAttempts}
                      nodesByTrackId={nodesByTrackId}
                      completedNodesByTrackId={completedNodesByTrackId}
                      dueSnippetIds={dueSnippetIds}
                      snippetStatsById={snippetStatsById}
                      trackProgress={trackProgress}
                      isPremiumUser={isPremiumUser}
                      scopeHeader={roleScopeHeader}
                      emptyMessage={
                        orderedTracksForRole.length === 0 && !showListLoading
                          ? 'Tracks for this role are still publishing.'
                          : null
                      }
                      isLoadingTracks={showListLoading}
                      isLoadingNodes={Boolean(selectedTrack && isLoadingNodes)}
                      footerContext={listFooterContext}
                      showRolePathToggle={canSetRolePath}
                      isPrimaryRolePath={isViewingPrimaryRolePath}
                      isSettingRolePath={isSettingPrimaryRole}
                      onSetAsRolePath={handleSetAsRolePath}
                      onSelectTrack={handleSelectTrack}
                      onStudySnippet={handleStudySnippet}
                      onStudyNode={handleStudyNode}
                      onStartNode={handleStartNode}
                      onContinueNode={handleContinueNode}
                      onPracticeStarted={handlePracticeStartedTrack}
                      onReviewDueSnippet={handleReviewDueSnippet}
                      loadingNodeAction={loadingNodeAction}
                      onGoToReview={onGoToReview}
                      onContinueStudy={handleContinueStudy}
                      continueStudyLabel={footerTargetNode?.title || null}
                      continueStudyDisabled={!selectedTrack || !footerTargetNode}
                      {...roadmapListContinuePreviewProps}
                    />
                  ) : (
                    <div className="roadmap-view__split-placeholder">
                      <p>Select a role to view its tracks.</p>
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          ) : null}
        </main>
      )}
    </div>
  );
}

